import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { ChevronDown, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { ChatModelId, ChatModelOption, ReboMode, ReboModeOption } from '@auw/stores';
import { SelectDropdown } from '../../common/SelectDropdown';
import { useMcpApprovalModel } from './McpApprovalSelect';

const px = (value: string): number => Number.parseFloat(value) || 0;

/**
 * Composer control-row tiers, widest to narrowest:
 *  - full:    [＋][access select][model][mode][toggles]   (everything inline)
 *  - compact: [＋][access select][summary ▾]             (model/mode/toggles in the popup)
 *  - narrow:  [＋][summary ▾]                            (access select folds into the popup too)
 * Mode-only workspaces (GTC / shipping) never gain from `compact` because the
 * summary is exactly as wide as the mode select it replaces, so the maths
 * skips straight from `full` to `narrow` for them.
 */
export type ComposerTier = 'full' | 'compact' | 'narrow';

/** Marks the swappable children of InputRowLeft so the measurement can find them. */
export type ComposerSlot = 'access' | 'summary' | 'expanded';
export const COMPOSER_SLOT_ATTR = 'data-composer-slot';

/**
 * Natural (unshrunk, single-line) width of a control cluster. Works whether the
 * element is in flow (possibly shrunk by flexbox) or parked off-layout with
 * `width: max-content`: nowrap children report their content width through
 * scrollWidth even when clipped, so padding + children + gaps is the true
 * natural width, and offsetWidth wins when nothing is clipped.
 */
export function naturalWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const kids = Array.from(el.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement && getComputedStyle(child).display !== 'none',
  );
  const content = kids.reduce((sum, kid) => sum + Math.max(kid.scrollWidth, kid.offsetWidth), 0)
    + Math.max(0, kids.length - 1) * px(style.columnGap);
  const chrome = px(style.paddingLeft) + px(style.paddingRight) + px(style.borderLeftWidth) + px(style.borderRightWidth);
  return Math.max(el.offsetWidth, content + chrome);
}

/**
 * Pure geometry: pick the widest tier whose cluster fits left of the send
 * column. Every input is a natural width or a row constant, none depends on the
 * tier currently rendered, so the decision cannot oscillate. Slots that are
 * parked off-layout (visibility:hidden; position:absolute; width:max-content)
 * still measure, which is what keeps the estimate for the *other* tiers alive.
 */
export function measureComposerTier(row: HTMLElement, left: HTMLElement): ComposerTier {
  const rowStyle = getComputedStyle(row);
  const rowInner = row.clientWidth - px(rowStyle.paddingLeft) - px(rowStyle.paddingRight);
  const last = row.lastElementChild;
  const send = last instanceof HTMLElement && last !== left ? last : null;
  const available = rowInner - (send?.offsetWidth ?? 0) - px(rowStyle.columnGap);
  const gap = px(getComputedStyle(left).columnGap);

  const slots: Partial<Record<ComposerSlot, number>> = {};
  let fixed = 0;
  let fixedCount = 0;
  for (const child of Array.from(left.children)) {
    if (!(child instanceof HTMLElement)) continue;
    const slot = child.getAttribute(COMPOSER_SLOT_ATTR) as ComposerSlot | null;
    if (slot) {
      slots[slot] = naturalWidth(child);
    } else {
      fixed += child.offsetWidth;
      fixedCount += 1;
    }
  }
  const cluster = (...parts: Array<number | undefined>) => {
    const present = parts.filter((part): part is number => typeof part === 'number');
    return fixed + present.reduce((sum, part) => sum + part, 0)
      + Math.max(0, fixedCount + present.length - 1) * gap;
  };
  const fits = (needed: number) => needed <= available + 0.5;

  if (fits(cluster(slots.access, slots.expanded))) return 'full';
  if (slots.summary !== undefined && fits(cluster(slots.access, slots.summary))) return 'compact';
  return 'narrow';
}

/**
 * Decide the control-row tier from real layout instead of a fixed card width,
 * so every workspace mode, locale and host (tablet / phone viewport, document
 * floating panel, GTC / shipping right pane, splitter drags) gets one clean
 * control line. Re-measures on card resize and whenever any slot's natural
 * width changes (locale switch, 'Ask for approval' → 'Full access', model or
 * mode label change).
 */
export function useComposerTier(enabled: boolean) {
  const cardRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const [tier, setTier] = useState<ComposerTier>('full');

  useLayoutEffect(() => {
    const card = cardRef.current;
    const row = rowRef.current;
    const left = leftRef.current;
    if (!enabled || !card || !row || !left) {
      setTier('full');
      return;
    }
    const update = () => setTier(measureComposerTier(row, left));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(card);
    left.querySelectorAll<HTMLElement>(`[${COMPOSER_SLOT_ATTR}]`).forEach((slot) => observer.observe(slot));
    return () => observer.disconnect();
  }, [enabled]);

  return { cardRef, rowRef, leftRef, tier: enabled ? tier : 'full' as ComposerTier };
}

interface CompactComposerSettingsProps {
  model: ChatModelId;
  modelOptions: ChatModelOption[];
  onModelChange: (model: ChatModelId) => void;
  /** Mirrors `showChatModelPicker` of the expanded row (agent / doc-agent). */
  showModel?: boolean;
  mode: ReboMode;
  modeOptions: ReboModeOption[];
  onModeChange: (mode: ReboMode) => void;
  reasoningEnabled: boolean;
  onToggleReasoning: () => void;
  /** Mirrors `reasoningSupported` of the expanded row. */
  showReasoning?: boolean;
  /** Interactive(HITL) switch — only the integrated agent supports it. */
  interactive?: { enabled: boolean; onToggle: () => void } | null;
  /** `narrow` tier: the MCP access select is folded into the popup as well. */
  access?: boolean;
  busy: boolean;
}

export function CompactComposerSettings({
  model, modelOptions, onModelChange, showModel = true, mode, modeOptions, onModeChange,
  reasoningEnabled, onToggleReasoning, showReasoning = true, interactive = null, access = false, busy,
}: CompactComposerSettingsProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const popupId = useId();
  const title = t('composerSettings.title');
  const modelLabel = modelOptions.find((option) => option.id === model)?.label ?? model;
  const modeOption = modeOptions.find((option) => option.id === mode);
  const summary = [showModel ? modelLabel : null, modeOption?.label ?? mode]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
  const reasoningState = t(reasoningEnabled ? 'composerSettings.on' : 'composerSettings.off');
  /* 같은 팝업의 Reasoning 과 어휘를 맞춘다 — zh-CN/zh-TW 는 interactive.on/off
   * ('开/关') 과 composerSettings.on/off ('开启/关闭') 가 달라 한 화면에 섞인다. */
  const interactiveState = t(interactive?.enabled ? 'composerSettings.on' : 'composerSettings.off');
  const approval = useMcpApprovalModel();
  const description = [
    `${title}: ${summary}`,
    access ? `${approval.title} ${approval.selectedLabel}` : null,
    interactive ? `${t('interactive.label')} ${interactiveState}` : null,
    showReasoning ? `${t('reasoning.label')} ${reasoningState}` : null,
  ].filter(Boolean).join(', ');

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!open || !trigger || !popup) return;
    let active = true;
    let focused = false;
    const cleanupPosition = autoUpdate(trigger, popup, () => {
      void computePosition(trigger, popup, {
        placement: 'top-start',
        strategy: 'fixed',
        middleware: [
          offset(8),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply({ availableWidth, availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                maxWidth: `${Math.max(0, availableWidth)}px`,
                maxHeight: `${Math.max(0, availableHeight)}px`,
              });
            },
          }),
        ],
      }).then(({ x, y }) => {
        if (!active) return;
        Object.assign(popup.style, { left: `${x}px`, top: `${y}px`, visibility: 'visible' });
        if (!focused) {
          popup.querySelector<HTMLButtonElement>('button[aria-haspopup="listbox"]')?.focus({ preventScroll: true });
          focused = true;
        }
      });
    });
    const handleOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!trigger.contains(target) && !popup.contains(target)) setOpen(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => {
      active = false;
      cleanupPosition();
      document.removeEventListener('pointerdown', handleOutside);
    };
  }, [open]);

  const closeAndFocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <>
      <SummaryButton
        ref={triggerRef}
        type="button"
        data-composer-compact-summary=""
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-label={description}
        title={description}
        onClick={() => setOpen((value) => !value)}
      >
        <SummaryLabel>{summary}</SummaryLabel>
        {showReasoning && (
          <ReasoningIndicator $on={reasoningEnabled} aria-hidden="true"><Sparkles size={13} /></ReasoningIndicator>
        )}
        <ChevronDown size={12} aria-hidden="true" />
      </SummaryButton>
      {open && createPortal(
        <SettingsPopup
          ref={popupRef}
          id={popupId}
          role="dialog"
          aria-label={title}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              closeAndFocus();
            }
          }}
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (!event.currentTarget.contains(next) && !triggerRef.current?.contains(next)) setOpen(false);
          }}
        >
          <PopupHeader>
            <span>{title}</span>
            <CloseButton type="button" aria-label={t('button.close')} onClick={closeAndFocus}><X size={16} /></CloseButton>
          </PopupHeader>
          {access && (
            <SettingField>
              <span>{approval.title}</span>
              <SelectDropdown
                aria-label={approval.title}
                size="sm"
                placement="inline"
                value={approval.value}
                options={approval.options.map((option) => ({
                  value: option.id,
                  label: option.label,
                  description: option.description,
                }))}
                onChange={(value) => {
                  const option = approval.options.find((entry) => entry.id === value);
                  if (option) approval.onChange(option.id);
                }}
              />
            </SettingField>
          )}
          {showModel && (
            <SettingField>
              <span>{t('chatModel.header')}</span>
              <SelectDropdown
                aria-label={t('chatModel.header')}
                size="sm"
                placement="inline"
                value={model}
                options={modelOptions.map((option) => ({
                  value: option.id,
                  label: option.label,
                  description: option.description,
                  disabled: option.disabled,
                }))}
                onChange={(value) => {
                  const option = modelOptions.find((entry) => entry.id === value);
                  if (option && !option.disabled) onModelChange(option.id);
                }}
              />
            </SettingField>
          )}
          <SettingField>
            <span>{t('composerSettings.mode')}</span>
            <SelectDropdown
              aria-label={t('composerSettings.mode')}
              size="sm"
              placement="inline"
              value={mode}
              options={modeOptions.map((option) => ({
                value: option.id,
                label: option.label,
                description: option.description,
              }))}
              onChange={(value) => {
                const option = modeOptions.find((entry) => entry.id === value);
                if (!option) return;
                onModeChange(option.id);
                if (option.id === 'skill') setOpen(false);
              }}
            />
            <FieldHint>{modeOption?.description}</FieldHint>
          </SettingField>
          {interactive && (
            <SwitchButton
              type="button"
              role="switch"
              aria-label={t('interactive.label')}
              aria-checked={interactive.enabled}
              disabled={busy}
              onClick={interactive.onToggle}
              title={t(interactive.enabled ? 'interactive.tooltipOn' : 'interactive.tooltipOff')}
            >
              <span>{t('interactive.label')}</span>
              <SwitchState>{interactiveState}<SwitchTrack $on={interactive.enabled} aria-hidden="true" /></SwitchState>
            </SwitchButton>
          )}
          {showReasoning && (
            <SwitchButton
              type="button"
              role="switch"
              aria-label={t('reasoning.label')}
              aria-checked={reasoningEnabled}
              disabled={busy}
              onClick={onToggleReasoning}
              title={t(reasoningEnabled ? 'reasoning.tooltipOn' : 'reasoning.tooltipOff')}
            >
              <span>{t('reasoning.label')}</span>
              <SwitchState>{reasoningState}<SwitchTrack $on={reasoningEnabled} aria-hidden="true" /></SwitchState>
            </SwitchButton>
          )}
        </SettingsPopup>,
        document.body,
      )}
    </>
  );
}

/* 펼친 행의 ComposerSelect 트리거(ReboModeTrigger: padding 4px 2px, gap 4px)와
 * 같은 치수를 쓴다. 요약 버튼이 대체하는 셀렉터보다 넓어지면 접어도 폭을
 * 못 벌어 mode 만 있는 워크스페이스(GTC/선적)에서 접기가 손해가 된다. */
const SummaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  padding: 4px 2px;
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  &:hover, &[aria-expanded='true'] { color: ${({ theme }) => theme.colors.textPrimary}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.accent}; outline-offset: 2px; }
  > svg { flex-shrink: 0; }
`;
const SummaryLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ReasoningIndicator = styled.span<{ $on: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ $on, theme }) => $on ? theme.colors.brand : theme.colors.textMuted};
  opacity: ${({ $on }) => $on ? 1 : 0.45};
`;
const SettingsPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: 121;
  width: 288px;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  font-size: 0.8125rem;
`;
const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
`;
const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.accent}; outline-offset: 2px; }
`;
const SettingField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;
const FieldHint = styled.span`
  font-size: 0.75rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textMuted};
`;
const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 36px;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: default; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.accent}; outline-offset: 2px; }
`;
const SwitchState = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const SwitchTrack = styled.span<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  width: 28px;
  height: 16px;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${({ $on, theme }) => $on ? theme.colors.brand : theme.colors.border};
  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    transform: translateX(${({ $on }) => $on ? 12 : 0}px);
    transition: transform 0.15s ease;
  }
`;
